import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip,
    CircularProgress, Snackbar, Alert
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api } from '../../helpers'

interface FirmwareVersion {
    version: string
    size: number
    builtAt?: string
    isActive: boolean
    deviceCount: number
}

interface DeviceFirmware {
    tableId: string
    name: string
    placeName?: string
    owner?: string
    firmwareVersion: string | null
    firmwareReportedAt: string | null
    firmwareOtaError: string | null
    isOnline: boolean
}

interface Overview {
    target: { version: string, setAt?: string } | null
    versions: FirmwareVersion[]
    devices: DeviceFirmware[]
    summary: { total: number, online: number, onTarget: number }
}

// OTA firmware management (spotpool_server controllers/firmware.ts). Releases are created with
// `yarn ota:release` on the server repo; this screen only picks which one every device runs.
const Firmware: FC = () => {
    const [overview, setOverview] = useState<Overview | null>(null)
    const [loading, setLoading] = useState(true)
    const [confirmVersion, setConfirmVersion] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null)

    const load = () => {
        setLoading(true)
        Api({ controller: 'Firmware', action: 'getFirmwareOverview' })
            .then((resp: any) => {
                if (resp?.error) setToast({ type: 'error', message: resp.error.message })
                else setOverview(resp)
            })
            .catch(() => setToast({ type: 'error', message: 'Could not load firmware versions.' }))
            .finally(() => setLoading(false))
    }

    const setTarget = () => {
        if (!confirmVersion) return
        setSaving(true)
        Api({ controller: 'Firmware', action: 'setTargetFirmware', version: confirmVersion })
            .then((resp: any) => {
                if (resp?.error) setToast({ type: 'error', message: resp.error.message })
                else {
                    setToast({ type: 'success', message: resp.message })
                    load()
                }
            })
            .catch(() => setToast({ type: 'error', message: 'Could not change the firmware version.' }))
            .finally(() => { setSaving(false); setConfirmVersion(null) })
    }

    useEffect(() => { load() }, [])

    const target = overview?.target?.version
    const summary = overview?.summary

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Firmware</Typography>
                <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                    Over-the-air updates for table gadgets. New versions are added with <code>yarn ota:release</code> on the
                    server. Choosing a version updates every online gadget now; offline ones update when they reconnect.
                </Typography>
            </Box>

            <Show>
                <Show.When isTrue={loading && !overview}>
                    <EmptyView loading={true} oopTxt='No Firmware' text='No firmware versions found.' />
                </Show.When>
                <Show.Else>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', px: '5px', mb: 2 }}>
                        <Paper sx={{ p: 2, minWidth: 200 }}>
                            <Typography sx={{ color: '#6b7280', fontSize: 13 }}>Current version</Typography>
                            <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>{target || 'Not set'}</Typography>
                        </Paper>
                        <Paper sx={{ p: 2, minWidth: 200 }}>
                            <Typography sx={{ color: '#6b7280', fontSize: 13 }}>Devices on current version</Typography>
                            <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>
                                {summary ? `${summary.onTarget} / ${summary.total}` : '-'}
                            </Typography>
                        </Paper>
                        <Paper sx={{ p: 2, minWidth: 200 }}>
                            <Typography sx={{ color: '#6b7280', fontSize: 13 }}>Online now</Typography>
                            <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>{summary ? summary.online : '-'}</Typography>
                        </Paper>
                    </Box>

                    <Typography sx={{ fontWeight: 'bold', px: '5px', mb: 1, textAlign: 'left' }}>Available versions</Typography>
                    <TableContainer component={Paper} sx={{ mx: '5px', mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {['Version', 'Size', 'Released', 'Devices running it', ''].map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!overview?.versions.length ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                            No firmware released yet. Run <code>yarn ota:release</code> in the server repo.
                                        </TableCell>
                                    </TableRow>
                                ) : overview.versions.map(v => (
                                    <TableRow key={v.version} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{v.version}</TableCell>
                                        <TableCell>{(v.size / 1024).toFixed(0)} KB</TableCell>
                                        <TableCell>{v.builtAt ? new Date(v.builtAt).toLocaleString() : '-'}</TableCell>
                                        <TableCell>{v.deviceCount}</TableCell>
                                        <TableCell align='right'>
                                            {v.isActive
                                                ? <Chip label='Current' color='success' size='small' />
                                                : (
                                                    <Button variant='outlined' size='small' onClick={() => setConfirmVersion(v.version)}>
                                                        Update to this version
                                                    </Button>
                                                )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography sx={{ fontWeight: 'bold', px: '5px', mb: 1, textAlign: 'left' }}>Devices</Typography>
                    <TableContainer component={Paper} sx={{ mx: '5px' }}>
                        <Table size='small'>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {['Table', 'Place', 'Owner', 'Firmware', 'Status'].map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(overview?.devices || []).map(d => (
                                    <TableRow key={d.tableId} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{d.name}</TableCell>
                                        <TableCell>{d.placeName || '-'}</TableCell>
                                        <TableCell>{d.owner || '-'}</TableCell>
                                        <TableCell>
                                            {d.firmwareVersion || <Typography component='span' sx={{ color: '#9ca3af', fontSize: 14 }}>Not reported</Typography>}
                                            {d.firmwareOtaError && (
                                                <Typography sx={{ color: '#ef4444', fontSize: 12 }}>Update failed: {d.firmwareOtaError}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size='small'
                                                label={!target ? (d.isOnline ? 'Online' : 'Offline')
                                                    : d.firmwareVersion === target ? 'Up to date'
                                                        : d.isOnline ? 'Updating' : 'Offline - updates on reconnect'}
                                                color={target && d.firmwareVersion === target ? 'success' : d.isOnline ? 'warning' : 'default'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>

            <Dialog open={Boolean(confirmVersion)} onClose={() => !saving && setConfirmVersion(null)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Update all devices to {confirmVersion}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Every online gadget will download and install firmware {confirmVersion} now and restart (about a minute,
                        during which its table can't be opened). Offline gadgets will update as soon as they reconnect.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmVersion(null)} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={setTarget}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Update to this version
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={5000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={toast?.type || 'success'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Firmware
